import { useEffect, useState } from "react";
import { Database, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { ProgressBar } from "../ProgressBar";
import { toast } from "@shared/hooks/use-toast";
import { PageHeader } from "@shared/components/PageHeader";

const StartMigration = ({
  onStartMigration,
  onPrevious,
  onNext,
  uploadId: propUploadId,
  utilityId: propUtilityId,
  isLoading = false,
}) => {
  const [isStarting, setIsStarting] = useState(false);
  const { state } = useLocation();

  const uploadId = state?.uploadId || propUploadId;
  const utilityId = state?.remote_utility_id || propUtilityId;

  const handleStartClick = async () => {
    setIsStarting(true);
    try {
      // ✅ No payload needed, just call the function
      await onStartMigration();
    } catch (error) {
      toast({
        title: `Failed to start migration:, ${error}`,
        description: ``,
      });
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="min-h-screen w-full p-6">
      <ProgressBar
        currentStep={6}
        totalSteps={7}
        progress={84}
        currentStepName={"Migration Execution"}
      />

      <div className="w-full flex flex-col gap-5">
        {/* Header */}
        <div>
          <PageHeader
            title="Migration Execution"
            subtitle="Execute the migration process and monitor its progress."
          />
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="mb-8">
            <div className="inline-flex p-4 bg-blue-100 rounded-full mb-6">
              <Database size={48} className="text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Ready to Start Migration
            </h2>
            <p className="text-gray-600 mb-8">
              You're about to migrate records to SMART360.
            </p>

            <button
              onClick={handleStartClick}
              disabled={isStarting || isLoading}
              className="px-8 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2 mx-auto bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(isStarting || isLoading) && (
                <Loader2 size={16} className="animate-spin" />
              )}
              <span>
                {isStarting
                  ? "Starting Migration..."
                  : isLoading
                  ? "Loading..."
                  : "Start Migration"}
              </span>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={onPrevious}
            disabled={isStarting || isLoading}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={16} />
            <span>Previous</span>
          </button>
          <button
            onClick={onNext}
            disabled={isStarting || isLoading}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>Next</span>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default StartMigration;
